import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  SkipForward,
  Sparkles,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Loader2,
  Bot,
  FileText,
  Keyboard,
  Undo2,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  useReviewSources,
  useReviewDecisions,
  useSubmitDecision,
  useUpdateScreeningPriority,
} from '@/features/review/hooks/useReview';
import { useAuthStore } from '@/store/authStore';
import type {
  Review,
  ReviewDecisionType,
  ReviewSettings,
  DecisionSource,
} from '@/features/review/types';
import { DEFAULT_REVIEW_SETTINGS } from '@/features/review/types';

interface ScreeningQueueProps {
  review: Review;
  phase: 'screening' | 'full_text';
  className?: string;
  onConsecutiveExcludesChange?: (count: number) => void;
}

export function ScreeningQueue({ review, phase, className, onConsecutiveExcludesChange }: ScreeningQueueProps) {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const settings: ReviewSettings = {
    ...DEFAULT_REVIEW_SETTINGS,
    ...(review.settings as Partial<ReviewSettings>),
  };

  const { data: reviewSources, isLoading } = useReviewSources(review.id, phase);
  const { data: allDecisions } = useReviewDecisions(review.id, phase);
  const submitDecision = useSubmitDecision();
  const updatePriority = useUpdateScreeningPriority();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [decisionCount, setDecisionCount] = useState(0);
  const [decisionHistory, setDecisionHistory] = useState<Array<{ sourceId: string; index: number }>>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sort sources by priority if configured
  const sortedSources = useMemo(() => {
    if (!reviewSources) return [];
    if (settings.screening_order === 'priority') {
      return [...reviewSources].sort(
        (a, b) => (b.screening_priority ?? 0) - (a.screening_priority ?? 0),
      );
    }
    return reviewSources;
  }, [reviewSources, settings.screening_order]);

  // Filter out sources already decided by current user (human decisions only)
  const myDecisions = useMemo(() => {
    if (!allDecisions || !user) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const d of allDecisions) {
      if (d.reviewer_id === user.id && d.decision_source !== 'ai') {
        map.set(d.source_id, d.decision);
      }
    }
    return map;
  }, [allDecisions, user]);

  // Track consecutive excludes
  const consecutiveExcludes = useMemo(() => {
    if (!allDecisions || !user) return 0;
    const humanDecisions = allDecisions
      .filter((d) => d.reviewer_id === user.id && d.decision_source !== 'ai')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    let count = 0;
    for (const d of humanDecisions) {
      if (d.decision === 'exclude') {
        count++;
      } else {
        break;
      }
    }
    return count;
  }, [allDecisions, user]);

  // Notify parent of consecutive excludes
  useEffect(() => {
    onConsecutiveExcludesChange?.(consecutiveExcludes);
  }, [consecutiveExcludes, onConsecutiveExcludesChange]);

  // Sources needing review by current user
  const pendingSources = useMemo(
    () => sortedSources.filter((s) => !myDecisions.has(s.source_id)),
    [sortedSources, myDecisions],
  );

  const currentSource = pendingSources[currentIndex] ?? null;
  const progress = sortedSources.length > 0
    ? ((sortedSources.length - pendingSources.length) / sortedSources.length) * 100
    : 0;

  // AI decision for this source
  const aiDecision = useMemo(() => {
    if (!allDecisions || !currentSource) return null;
    return allDecisions.find(
      (d) => d.source_id === currentSource.source_id && d.decision_source === 'ai',
    );
  }, [allDecisions, currentSource]);

  const handleDecision = useCallback(
    async (decision: ReviewDecisionType) => {
      if (!currentSource || !user) return;

      // Check if exclusion reason required
      if (
        decision === 'exclude' &&
        settings.require_exclusion_reason &&
        !selectedReason
      ) {
        toast.error('Please select an exclusion reason');
        return;
      }

      const decisionSource: DecisionSource =
        review.review_mode === 'ai_human' && aiDecision
          ? 'ai_confirmed'
          : 'human';

      try {
        await submitDecision.mutateAsync({
          review_id: review.id,
          source_id: currentSource.source_id,
          decision,
          phase,
          decision_source: decisionSource,
          exclusion_reason_category: decision === 'exclude' ? selectedReason || undefined : undefined,
          reason: decision === 'exclude' ? selectedReason || undefined : undefined,
        });
        setDecisionHistory((prev) => [...prev, { sourceId: currentSource.source_id, index: currentIndex }]);
        setSelectedReason('');
        setDecisionCount((c) => c + 1);
        // Move to next
        if (currentIndex >= pendingSources.length - 1) {
          setCurrentIndex(0);
        }
      } catch {
        // Error handled by mutation
      }
    },
    [currentSource, user, review, phase, settings, selectedReason, aiDecision, submitDecision, currentIndex, pendingSources.length],
  );

  const handleUndo = useCallback(async () => {
    if (decisionHistory.length === 0 || !user) return;
    const last = decisionHistory[decisionHistory.length - 1]!;

    // Delete the last human decision
    const { error } = await supabase
      .from('review_decisions')
      .delete()
      .eq('review_id', review.id)
      .eq('source_id', last.sourceId)
      .eq('reviewer_id', user.id)
      .neq('decision_source', 'ai');

    if (!error) {
      setDecisionHistory((prev) => prev.slice(0, -1));
      setCurrentIndex(last.index);
      setDecisionCount((c) => Math.max(0, c - 1));
      queryClient.invalidateQueries({ queryKey: ['review-decisions', review.id] });
      queryClient.invalidateQueries({ queryKey: ['review-stats', review.id] });
      toast.success('Decision undone');
    }
  }, [decisionHistory, user, review.id, queryClient]);

  const handleRecalculatePriority = useCallback(async () => {
    try {
      await updatePriority.mutateAsync(review.id);
      setDecisionCount(0);
    } catch {
      // Error handled by mutation
    }
  }, [updatePriority, review.id]);

  // Keep a ref to handleUndo so the keyboard handler always calls the latest version,
  // avoiding stale closure when pendingSources.length transitions to 0 mid-flight.
  const handleUndoRef = useRef(handleUndo);
  useEffect(() => {
    handleUndoRef.current = handleUndo;
  }, [handleUndo]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Ignore when typing in input/select
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if ((e.target as HTMLElement).getAttribute('role') === 'combobox') return;

      if (submitDecision.isPending) return;

      switch (e.key.toLowerCase()) {
        case 'i':
        case 'y':
          e.preventDefault();
          handleDecision('include');
          break;
        case 'e':
        case 'n':
          e.preventDefault();
          handleDecision('exclude');
          break;
        case 'm':
          e.preventDefault();
          handleDecision('maybe');
          break;
        case 'arrowleft':
          e.preventDefault();
          setCurrentIndex((prev) => Math.max(0, prev - 1));
          break;
        case 'arrowright':
          e.preventDefault();
          setCurrentIndex((prev) => Math.min(pendingSources.length - 1, prev + 1));
          break;
        case 'z':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleUndoRef.current();
          }
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDecision, submitDecision.isPending, pendingSources.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (pendingSources.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <CheckCircle2 className="h-10 w-10 text-emerald-400 mb-3" />
          <p className="text-sm font-medium text-slate-700">All sources reviewed!</p>
          <p className="text-xs text-slate-500 mt-1">
            {sortedSources.length} source{sortedSources.length !== 1 ? 's' : ''} screened in this phase
          </p>
        </CardContent>
      </Card>
    );
  }

  const source = currentSource?.source;

  return (
    <div ref={containerRef} className={cn('space-y-4', className)}>
      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            Source {sortedSources.length - pendingSources.length + currentIndex + 1} / {sortedSources.length}
          </span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Recalculate priority suggestion */}
      {settings.screening_order === 'priority' && decisionCount > 0 && decisionCount % 10 === 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-50 border border-violet-100">
          <Sparkles className="h-4 w-4 text-violet-500 shrink-0" />
          <span className="text-xs text-violet-700">
            {decisionCount} decisions since last update. Recalculate priorities for better ordering?
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto text-xs h-7 border-violet-200 text-violet-700 hover:bg-violet-100"
            onClick={handleRecalculatePriority}
            disabled={updatePriority.isPending}
          >
            {updatePriority.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <Sparkles className="h-3 w-3 mr-1" />
            )}
            Recalculate
          </Button>
        </div>
      )}

      {/* Source card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 leading-tight">
                {source?.title || source?.file_name || 'Untitled'}
              </h3>
              {source?.authors && source.authors.length > 0 && (
                <p className="text-xs text-slate-500 truncate">
                  {source.authors.slice(0, 3).join(', ')}
                  {source.authors.length > 3 && ` +${source.authors.length - 3}`}
                </p>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {source?.publication_year && (
                  <Badge variant="outline" className="text-[10px]">
                    {source.publication_year}
                  </Badge>
                )}
                {source?.journal && (
                  <Badge variant="secondary" className="text-[10px] truncate max-w-[200px]">
                    {source.journal}
                  </Badge>
                )}
                {source?.doi && (
                  <Badge variant="outline" className="text-[10px]">
                    DOI
                  </Badge>
                )}
                {currentSource?.screening_priority != null && currentSource.screening_priority > 0 && (
                  <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200">
                    Priority: {Math.round(currentSource.screening_priority * 100)}%
                  </Badge>
                )}
                {/* AI Score badge */}
                {aiDecision && (
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px]',
                      aiDecision.decision === 'include' && 'text-emerald-600 border-emerald-200 bg-emerald-50',
                      aiDecision.decision === 'maybe' && 'text-amber-600 border-amber-200 bg-amber-50',
                      aiDecision.decision === 'exclude' && 'text-rose-600 border-rose-200 bg-rose-50',
                    )}
                  >
                    <Bot className="mr-0.5 h-3 w-3" />
                    AI: {aiDecision.decision}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Abstract */}
          {source?.abstract ? (
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs font-medium text-slate-500 mb-1">Abstract</p>
              <p className="text-sm text-slate-700 leading-relaxed line-clamp-[12]">
                {source.abstract}
              </p>
            </div>
          ) : (
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <FileText className="h-5 w-5 text-slate-300 mx-auto mb-1" />
              <p className="text-xs text-slate-400">No abstract available</p>
            </div>
          )}

          {/* AI decision hint */}
          {aiDecision && aiDecision.reason && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
              <Bot className="h-4 w-4 text-blue-500 shrink-0" />
              <span className="text-xs text-blue-700">
                AI reason: {aiDecision.reason}
              </span>
            </div>
          )}

          {/* Exclusion reason selector */}
          {settings.require_exclusion_reason && settings.exclusion_reasons.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-slate-500">Exclusion reason (required for exclude):</p>
              <Select value={selectedReason} onValueChange={setSelectedReason}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  {settings.exclusion_reasons.map((reason) => (
                    <SelectItem key={reason} value={reason} className="text-xs">
                      {reason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Decision buttons */}
          <TooltipProvider>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                    onClick={() => handleDecision('include')}
                    disabled={submitDecision.isPending}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1.5" />
                    Include
                  </Button>
                </TooltipTrigger>
                <TooltipContent><kbd>I</kbd> or <kbd>Y</kbd></TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                    onClick={() => handleDecision('maybe')}
                    disabled={submitDecision.isPending}
                  >
                    <HelpCircle className="h-4 w-4 mr-1.5" />
                    Maybe
                  </Button>
                </TooltipTrigger>
                <TooltipContent><kbd>M</kbd></TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                    onClick={() => handleDecision('exclude')}
                    disabled={submitDecision.isPending}
                  >
                    <XCircle className="h-4 w-4 mr-1.5" />
                    Exclude
                  </Button>
                </TooltipTrigger>
                <TooltipContent><kbd>E</kbd> or <kbd>N</kbd></TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          {/* Navigation + undo */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0}
              >
                <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                Previous
              </Button>
              {decisionHistory.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-slate-400"
                  onClick={handleUndo}
                >
                  <Undo2 className="h-3.5 w-3.5 mr-1" />
                  Undo
                </Button>
              )}
            </div>
            <span className="text-xs text-slate-400">
              {currentIndex + 1} of {pendingSources.length} pending
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() =>
                setCurrentIndex(Math.min(pendingSources.length - 1, currentIndex + 1))
              }
              disabled={currentIndex >= pendingSources.length - 1}
            >
              {currentIndex < pendingSources.length - 1 ? (
                <>
                  Skip
                  <SkipForward className="h-3.5 w-3.5 ml-1" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Keyboard shortcuts hint */}
          <div className="flex items-center justify-center gap-1 text-[10px] text-slate-400">
            <Keyboard className="h-3 w-3" />
            <span>
              <kbd className="px-1 rounded bg-slate-100 border text-[10px]">I</kbd> Include
              {' '}
              <kbd className="px-1 rounded bg-slate-100 border text-[10px]">M</kbd> Maybe
              {' '}
              <kbd className="px-1 rounded bg-slate-100 border text-[10px]">E</kbd> Exclude
              {' '}
              <kbd className="px-1 rounded bg-slate-100 border text-[10px]">Ctrl+Z</kbd> Undo
            </span>
          </div>

          {/* Consecutive exclude counter */}
          {consecutiveExcludes >= 5 && (
            <div className={cn(
              'rounded-lg px-3 py-2 text-xs',
              consecutiveExcludes >= 20
                ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border border-slate-200 bg-slate-50 text-slate-600',
            )}>
              {consecutiveExcludes >= 20
                ? `Safe to stop — ${consecutiveExcludes} consecutive excludes (95%+ relevant sources likely found)`
                : `${consecutiveExcludes} consecutive excludes`}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
